import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { Banner, Icon, ModalContext, Sizes } from '@taxdown/piccolo-web';
import { YayoyakiFormatter } from '@taxdown/kami-sama/dist/kamiSama/yayoyakiFormatter';
import { locizeNS } from 'Services/i18n';
import { userStageSelector } from 'Store/modules/validations/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import CustomTransition from 'Components/CustomTransition';
import { DomiciliationType } from 'Components/DomiciliationType';
import { stages } from 'Constants/stages';
import { openLinkInNewTab } from 'Utils/links';
import { SeasonFiltered } from 'Types/common';
import { useGetDomiciliationBannerInfo } from './hooks';

const DomiciliationBanner = ({ tax }: { tax: SeasonFiltered }): JSX.Element | null => {
  const { t } = useTranslation(locizeNS.DASHBOARD);
  const userStage = useSelector(userStageSelector);
  const {
    isManualPayment,
    domiciliationIsAuthorized,
    isCard,
    needAuthorization,
    isLoading,
    userChargements,
    fee,
  } = useGetDomiciliationBannerInfo();
  const { showModal } = useContext(ModalContext);
  const handleClick = (): void => {
    showModal({
      comp: <DomiciliationType />,
    });
  };

  if (isLoading) return null;

  const hasMultipleFiling =
    tax?.filingInfo?.filedTimes && tax?.filingInfo?.filedTimes > 1 && userStage === stages.FILED;

  return (
    <CustomTransition start>
      {domiciliationIsAuthorized && (
        <Banner
          id="domiciliation-success-banner"
          label={t('DomiciliationBanner.success.label', {
            context: isCard && 'card',
            fee: YayoyakiFormatter.formatAmountMxn(fee),
          })}
          size={Sizes.M}
          layout="embed"
          position="vertically"
          mode="success"
          icon={<Icon icon="InfoSolidXS" color="success" />}
          hasIcon
          titleSize={Sizes.S}
        />
      )}
      {isManualPayment && (
        <Banner
          id="domiciliation-manual-payment-user-banner"
          label={t('DomiciliationBanner.manualPayment.label')}
          description={t('DomiciliationBanner.manualPayment.description')}
          size={Sizes.M}
          layout="embed"
          position="vertically"
          mode="warning"
          icon={<Icon icon="InfoSolidXS" color="warning" />}
          hasIcon
          {...(userChargements?.latestChargement?.manualPaymentLink
            ? {
                buttonLabel: t('DomiciliationBanner.manualPayment.buttonLabel'),
                onClick: () =>
                  userChargements?.latestChargement?.manualPaymentLink &&
                  openLinkInNewTab(userChargements?.latestChargement?.manualPaymentLink),
              }
            : {})}
          titleSize={Sizes.S}
        />
      )}
      {hasMultipleFiling && needAuthorization && (
        <Banner
          id="domiciliation-banner"
          label={t('DomiciliationBanner.label')}
          size={Sizes.M}
          layout="embed"
          position="vertically"
          description={t('DomiciliationBanner.description')}
          mode="error"
          icon={<Icon icon="InfoSolidXS" color="error" />}
          hasIcon
          buttonLabel={t('DomiciliationBanner.buttonLabel')}
          onClick={handleClick}
          titleSize={Sizes.S}
        />
      )}
    </CustomTransition>
  );
};

export default DomiciliationBanner;
